/**
 * Audit Service
 * 
 * Manages audits, findings, incidents, and corrective actions via Thalassa API.
 * Updated: 2025-12-17 - Migrated to Thalassa API
 */

import { Audit, Finding, Incident, CorrectiveAction } from "./index";
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const AUDITS_API = '/v1/audits';
const FINDINGS_API = '/v1/findings';
const INCIDENTS_API = '/v1/incidents';
const CORRECTIVE_ACTIONS_API = '/v1/corrective-actions';

export interface AuditServiceOptions {
  activeTenancy: { id: string };
}

export class AuditService {
  private tenancyId: string;

  constructor(options: AuditServiceOptions) {
    this.tenancyId = options.activeTenancy.id;
  }

  private getHeaders() {
    return {
      'X-Tenancy-Id': this.tenancyId,
      'Company-ID': this.tenancyId,
    };
  }

  // --- Audit CRUD ---
  async listAudits(): Promise<Audit[]> {
    console.log('[AuditService] Listing audits via Thalassa API');
    try {
      const response = await deiterateAPI.GET(AUDITS_API, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.audits) {
        return response.audits;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error listing audits:', error);
      return [];
    }
  }

  async getAudit(auditId: string): Promise<Audit | null> {
    console.log('[AuditService] Getting audit via Thalassa API:', auditId);
    try {
      const response = await deiterateAPI.GET(`${AUDITS_API}/${auditId}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.audit) {
        return response.audit;
      }
      return null;
    } catch (error) {
      console.error('[AuditService] Error getting audit:', error);
      return null;
    }
  }

  async createAudit(audit: Omit<Audit, "id">): Promise<string> {
    console.log('[AuditService] Creating audit via Thalassa API');
    const response = await deiterateAPI.POST(AUDITS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(audit),
    });

    if (response.statusCode === 201 && response.audit) {
      return response.audit.id;
    }
    throw new Error(response.error || 'Failed to create audit');
  }

  async updateAudit(auditId: string, data: Partial<Audit>) {
    console.log('[AuditService] Updating audit via Thalassa API:', auditId);
    const response = await deiterateAPI.PATCH(`${AUDITS_API}/${auditId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update audit');
    }
  }

  async deleteAudit(auditId: string) {
    console.log('[AuditService] Deleting audit via Thalassa API:', auditId);
    const response = await deiterateAPI.DELETE(`${AUDITS_API}/${auditId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete audit');
    }
  }

  // --- Findings (Non-Conformities) for Audits ---
  async listFindings(auditId: string): Promise<Finding[]> {
    console.log('[AuditService] Listing audit findings via Thalassa API:', auditId);
    try {
      const response = await deiterateAPI.GET(`${AUDITS_API}/${auditId}/findings`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.findings) {
        return response.findings;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error listing findings:', error);
      return [];
    }
  }

  async createFinding(auditId: string, finding: Omit<Finding, 'id'>) {
    console.log('[AuditService] Creating finding via Thalassa API for audit:', auditId);
    const response = await deiterateAPI.POST(`${AUDITS_API}/${auditId}/findings`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(finding),
    });

    if (response.statusCode === 201 && response.finding) {
      return response.finding.id;
    }
    throw new Error(response.error || 'Failed to create finding');
  }

  async updateFinding(auditId: string, findingId: string, data: Partial<Finding>) {
    console.log('[AuditService] Updating finding via Thalassa API:', findingId);
    // Ensure linkedDocuments is always an array
    const updateData = { ...data };
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }

    const response = await deiterateAPI.PATCH(`${AUDITS_API}/${auditId}/findings/${findingId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(updateData),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update finding');
    }
  }

  async deleteFinding(auditId: string, findingId: string) {
    console.log('[AuditService] Deleting finding via Thalassa API:', findingId);
    const response = await deiterateAPI.DELETE(`${AUDITS_API}/${auditId}/findings/${findingId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete finding');
    }
  }

  // --- Corrective Action Methods ---
  async listStandaloneCorrectiveActions(): Promise<CorrectiveAction[]> {
    console.log('[AuditService] Listing corrective actions via Thalassa API');
    try {
      const response = await deiterateAPI.GET(CORRECTIVE_ACTIONS_API, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.correctiveActions) {
        return response.correctiveActions;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error listing corrective actions:', error);
      return [];
    }
  }

  async getCorrectiveAction(actionId: string): Promise<CorrectiveAction | null> {
    console.log('[AuditService] Getting corrective action via Thalassa API:', actionId);
    try {
      const response = await deiterateAPI.GET(`${CORRECTIVE_ACTIONS_API}/${actionId}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.correctiveAction) {
        return response.correctiveAction;
      }
      return null;
    } catch (error) {
      console.error('[AuditService] Error getting corrective action:', error);
      return null;
    }
  }

  async createStandaloneCorrectiveAction(action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>): Promise<CorrectiveAction> {
    console.log('[AuditService] Creating corrective action via Thalassa API');
    const response = await deiterateAPI.POST(CORRECTIVE_ACTIONS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(action),
    });

    if (response.statusCode === 201 && response.correctiveAction) {
      return response.correctiveAction;
    }
    throw new Error(response.error || 'Failed to create corrective action');
  }

  async updateStandaloneCorrectiveAction(actionId: string, data: Partial<Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>>) {
    console.log('[AuditService] Updating corrective action via Thalassa API:', actionId);
    // Ensure linkedDocuments is always an array
    const updateData = { ...data };
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }
    if ('dateClosed' in data) {
      updateData.dateClosed = data.dateClosed || null;
    }

    // DED-1628: Handle findingId - convert "none" to null
    if ('findingId' in data) {
      updateData.findingId = data.findingId === 'none' ? null : (data.findingId || null);
    }

    // Remove undefined values to prevent Firestore errors
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

    console.log('[AuditService] updateStandaloneCorrectiveAction:', actionId, updateData);
    
    const response = await deiterateAPI.PATCH(`${CORRECTIVE_ACTIONS_API}/${actionId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(updateData),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update corrective action');
    }
  }

  async deleteStandaloneCorrectiveAction(actionId: string) {
    console.log('[AuditService] Deleting corrective action via Thalassa API:', actionId);
    const response = await deiterateAPI.DELETE(`${CORRECTIVE_ACTIONS_API}/${actionId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete corrective action');
    }
  }

  async linkCorrectiveActionToFinding(actionId: string, findingId: string) {
    console.log('[AuditService] Linking corrective action to finding via Thalassa API');
    const response = await deiterateAPI.POST(`${CORRECTIVE_ACTIONS_API}/${actionId}/link-finding`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify({ findingId }),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to link corrective action to finding');
    }
  }

  async unlinkCorrectiveAction(actionId: string) {
    console.log('[AuditService] Unlinking corrective action from finding via Thalassa API');
    const response = await deiterateAPI.POST(`${CORRECTIVE_ACTIONS_API}/${actionId}/unlink-finding`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to unlink corrective action');
    }
  }

  async unlinkCorrectiveActionFromAudit(actionId: string) {
    console.log('[AuditService] Unlinking corrective action from audit via Thalassa API');
    const response = await deiterateAPI.PATCH(`${CORRECTIVE_ACTIONS_API}/${actionId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify({ auditId: null }),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to unlink corrective action from audit');
    }
  }

  async getCorrectiveActionsForFinding(findingId: string): Promise<CorrectiveAction[]> {
    if (!findingId) return [];

    console.log('[AuditService] Getting corrective actions for finding via Thalassa API:', findingId);
    try {
      const response = await deiterateAPI.GET(`${CORRECTIVE_ACTIONS_API}?findingId=${findingId}`, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.correctiveActions) {
        return response.correctiveActions;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error getting corrective actions for finding:', error);
      return [];
    }
  }

  // --- Incidents ---
  async listIncidents(): Promise<Incident[]> {
    console.log('[AuditService] Listing incidents via Thalassa API');
    try {
      const response = await deiterateAPI.GET(INCIDENTS_API, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.incidents) {
        return response.incidents;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error listing incidents:', error);
      return [];
    }
  }

  async createIncident(incident: Omit<Incident, "id"> & { auditId?: string }): Promise<Incident> {
    console.log('[AuditService] Creating incident via Thalassa API');
    const response = await deiterateAPI.POST(INCIDENTS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(incident),
    });

    if (response.statusCode === 201 && response.incident) {
      return response.incident;
    }
    throw new Error(response.error || 'Failed to create incident');
  }

  async updateIncident(incidentId: string, data: Partial<Incident>) {
    console.log('[AuditService] Updating incident via Thalassa API:', incidentId);
    const response = await deiterateAPI.PATCH(`${INCIDENTS_API}/${incidentId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update incident');
    }
  }

  async deleteIncident(incidentId: string) {
    console.log('[AuditService] Deleting incident via Thalassa API:', incidentId);
    const response = await deiterateAPI.DELETE(`${INCIDENTS_API}/${incidentId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete incident');
    }
  }

  // --- Link/Unlink Incident to Audit ---
  async linkIncidentToAudit(incidentId: string, auditId: string) {
    console.log('[AuditService] Linking incident to audit via Thalassa API');
    await this.updateIncident(incidentId, { auditId } as any);
  }

  async unlinkIncidentFromAudit(incidentId: string) {
    console.log('[AuditService] Unlinking incident from audit via Thalassa API');
    await this.updateIncident(incidentId, { auditId: null } as any);
  }

  // --- Standalone Non-Conformities ---
  async listStandaloneFindings(): Promise<Finding[]> {
    console.log('[AuditService] Listing standalone findings via Thalassa API');
    try {
      const response = await deiterateAPI.GET(FINDINGS_API, {
        mode: 'cors',
        backend: 'thalassa',
        headers: this.getHeaders(),
      });

      if (response.statusCode === 200 && response.findings) {
        return response.findings;
      }
      return [];
    } catch (error) {
      console.error('[AuditService] Error listing standalone findings:', error);
      return [];
    }
  }

  // --- Standalone Incidents ---
  async listStandaloneIncidents(): Promise<Incident[]> {
    // Same as listIncidents since all incidents are in the same collection
    return this.listIncidents();
  }

  async createStandaloneFinding(finding: Omit<Finding, 'id'> & { auditId?: string }) {
    console.log('[AuditService] Creating standalone finding via Thalassa API');
    const response = await deiterateAPI.POST(FINDINGS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify({
        ...finding,
        auditId: finding.auditId || null,
      }),
    });

    if (response.statusCode === 201 && response.finding) {
      return response.finding.id;
    }
    throw new Error(response.error || 'Failed to create standalone finding');
  }

  async updateStandaloneFinding(findingId: string, data: Partial<Finding>) {
    console.log('[AuditService] Updating standalone finding via Thalassa API:', findingId);
    // Ensure linkedDocuments is always an array
    const updateData = { ...data };
    if ('linkedDocuments' in data) {
      updateData.linkedDocuments = Array.isArray(data.linkedDocuments) ? data.linkedDocuments : [];
    }

    const response = await deiterateAPI.PATCH(`${FINDINGS_API}/${findingId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
      body: JSON.stringify(updateData),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to update standalone finding');
    }
  }

  async deleteStandaloneFinding(findingId: string) {
    console.log('[AuditService] Deleting standalone finding via Thalassa API:', findingId);
    const response = await deiterateAPI.DELETE(`${FINDINGS_API}/${findingId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: this.getHeaders(),
    });

    if (response.statusCode !== 200) {
      throw new Error(response.error || 'Failed to delete standalone finding');
    }
  }

  // --- Link/Unlink Standalone Finding to Audit ---
  async linkStandaloneFindingToAudit(findingId: string, auditId: string) {
    await this.updateStandaloneFinding(findingId, { auditId } as any);
  }

  async unlinkStandaloneFindingFromAudit(findingId: string) {
    await this.updateStandaloneFinding(findingId, { auditId: null } as any);
  }

  // Move a finding from an audit to the standalone findings collection
  async moveFindingToStandalone(auditId: string, findingId: string): Promise<string> {
    console.log('[AuditService] Moving finding to standalone via Thalassa API');
    
    // Get the finding from the audit
    const findings = await this.listFindings(auditId);
    const finding = findings.find(f => f.id === findingId);

    if (!finding) {
      throw new Error(`Finding ${findingId} not found in audit ${auditId}`);
    }

    // Create standalone finding
    const { id, ...findingData } = finding;
    const newFindingId = await this.createStandaloneFinding({
      ...findingData,
      origin: 'standalone' as const,
      auditId: null
    });

    // Delete from audit's findings
    await this.deleteFinding(auditId, findingId);

    return newFindingId;
  }
}
